'use client'

import { useState } from 'react'
import Link from 'next/link'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import Button from '@/components/Button'
import { registerAction } from '@/app/actions/auth'
import { useAction } from 'next-safe-action/hooks'
import { trackSignUp } from '@/components/GoogleAnalytics'

function isValidCPF(raw: string): boolean {
  const cpf = raw.replace(/\D/g, '')
  if (cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) return false
  let sum = 0
  for (let i = 0; i < 9; i++) sum += Number(cpf[i]) * (10 - i)
  let check = 11 - (sum % 11)
  if (check >= 10) check = 0
  if (check !== Number(cpf[9])) return false
  sum = 0
  for (let i = 0; i < 10; i++) sum += Number(cpf[i]) * (11 - i)
  check = 11 - (sum % 11)
  if (check >= 10) check = 0
  return check === Number(cpf[10])
}

function toProperCase(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => (w.length > 0 ? w[0].toUpperCase() + w.slice(1).toLowerCase() : w))
    .join(' ')
}

const registerSchema = z.object({
  name: z.string().min(3, 'Nome deve ter pelo menos 3 caracteres').transform(toProperCase),
  email: z.string().email('Email inválido').transform((s) => s.toLowerCase()),
  password: z.string().min(8, 'Mínimo de 8 caracteres')
    .regex(/^(?=.*[A-Za-z])(?=.*\d).{8,}$/, 'Deve conter letras e números'),
  confirmPassword: z.string(),
  cpf: z.string()
    .min(11, 'CPF inválido')
    .max(14, 'CPF inválido')
    .refine(isValidCPF, 'CPF inválido. Verifique os dígitos.'),
  phone: z.string().min(10, 'Telefone inválido'),
  acceptTerms: z.preprocess(
    (val) => (val === true || val === 'on' || val === 'true') ? true : val,
    z.literal(true, {
      errorMap: () => ({ message: 'Você precisa aceitar os Termos de Uso e Política de Privacidade' }),
    }),
  ),
}).refine((data) => data.password === data.confirmPassword, {
  message: "As senhas não coincidem",
  path: ["confirmPassword"],
});

type RegisterFormData = z.infer<typeof registerSchema>

interface RegisterFormProps {
  onSuccess: (email: string) => void
  onResendNeeded: (email: string) => void
}

export default function RegisterForm({ onSuccess, onResendNeeded }: RegisterFormProps) {
  const [serverError, setServerError] = useState<string | null>(null)
  
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting }
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema)
  })

  const { execute, status } = useAction(registerAction, {
    onSuccess: (data, input) => {
      if (data?.success) {
        trackSignUp('email')
        onSuccess(data.email!)
      } else if (data?.error) {
        setServerError(data.error)
        if (data.canResend && input) {
          onResendNeeded((input as { email: string }).email)
        }
      }
    },
    onError: (error) => {
      const ve = (error as { validationErrors?: Record<string, { _errors?: string[] }> }).validationErrors
      if (ve) {
        const firstField = Object.keys(ve)[0]
        const msg = ve[firstField]?._errors?.[0]
        setServerError(msg || 'Dados inválidos. Verifique os campos.')
        return
      }
      setServerError('Erro de conexão. Tente novamente.')
    }
  })

  const onSubmit = (data: RegisterFormData) => {
    setServerError(null)
    execute(data)
  }

  // Máscaras
  const handleCPFChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, '')
    if (value.length > 11) value = value.slice(0, 11)
    value = value.replace(/(\d{3})(\d)/, '$1.$2')
    value = value.replace(/(\d{3})(\d)/, '$1.$2')
    value = value.replace(/(\d{3})(\d{1,2})$/, '$1-$2')
    setValue('cpf', value, { shouldValidate: true })
  }

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, '')
    if (value.length > 11) value = value.slice(0, 11)
    value = value.replace(/^(\d{2})(\d)/g, '($1) $2')
    value = value.replace(/(\d)(\d{4})$/, '$1-$2')
    setValue('phone', value, { shouldValidate: true })
  }

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue('email', e.target.value.toLowerCase(), { shouldValidate: true })
  }

  const isLoading = status === 'executing' || isSubmitting

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-white">Crie sua conta</h2>
        <p className="text-zinc-400 text-sm mt-1">Junte-se à Vibe hoje mesmo</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-zinc-300 mb-2">Nome completo</label>
        <input
          {...register('name')}
          className={`w-full px-4 py-3.5 min-h-[48px] text-base bg-zinc-800/50 border rounded-xl text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#ffa300] transition-all ${errors.name ? 'border-red-500' : 'border-zinc-700'}`}
          placeholder="Seu nome"
        />
        {errors.name && <p className="text-red-400 text-xs mt-1">{errors.name.message}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-zinc-300 mb-2">Email</label>
        <input
          {...register('email', { onChange: handleEmailChange })}
          type="email"
          className={`w-full px-4 py-3.5 min-h-[48px] text-base bg-zinc-800/50 border rounded-xl text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#ffa300] transition-all ${errors.email ? 'border-red-500' : 'border-zinc-700'}`}
          placeholder="seu@email.com"
        />
        {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email.message}</p>}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-zinc-300 mb-2">CPF</label>
          <input
            {...register('cpf')}
            onChange={handleCPFChange}
            className={`w-full px-4 py-3.5 min-h-[48px] text-base bg-zinc-800/50 border rounded-xl text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#ffa300] transition-all ${errors.cpf ? 'border-red-500' : 'border-zinc-700'}`}
            placeholder="000.000.000-00"
          />
          {errors.cpf && <p className="text-red-400 text-xs mt-1">{errors.cpf.message}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-zinc-300 mb-2">Telefone</label>
          <input
            {...register('phone')}
            onChange={handlePhoneChange}
            className={`w-full px-4 py-3.5 min-h-[48px] text-base bg-zinc-800/50 border rounded-xl text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#ffa300] transition-all ${errors.phone ? 'border-red-500' : 'border-zinc-700'}`}
            placeholder="(11) 99999-9999"
          />
          {errors.phone && <p className="text-red-400 text-xs mt-1">{errors.phone.message}</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-zinc-300 mb-2">Senha</label>
          <input
            {...register('password')}
            type="password"
            className={`w-full px-4 py-3.5 min-h-[48px] text-base bg-zinc-800/50 border rounded-xl text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#ffa300] transition-all ${errors.password ? 'border-red-500' : 'border-zinc-700'}`}
            placeholder="••••••••"
          />
          {errors.password && <p className="text-red-400 text-xs mt-1">{errors.password.message}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-zinc-300 mb-2">Confirmar</label>
          <input
            {...register('confirmPassword')}
            type="password"
            className={`w-full px-4 py-3.5 min-h-[48px] text-base bg-zinc-800/50 border rounded-xl text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#ffa300] transition-all ${errors.confirmPassword ? 'border-red-500' : 'border-zinc-700'}`}
            placeholder="••••••••"
          />
          {errors.confirmPassword && <p className="text-red-400 text-xs mt-1">{errors.confirmPassword.message}</p>}
        </div>
      </div>

      <div className="flex items-center gap-3">
        <input
          type="checkbox"
          id="acceptTerms"
          {...register('acceptTerms')}
          className="h-4 w-4 shrink-0 rounded border-zinc-600 bg-zinc-800/50 text-[#ffa300] focus:ring-2 focus:ring-[#ffa300] focus:ring-offset-0 focus:ring-offset-transparent"
        />
        <label htmlFor="acceptTerms" className="text-sm text-zinc-300 leading-tight cursor-pointer">
          Li e aceito os{' '}
          <Link href="/termos" target="_blank" rel="noopener noreferrer" className="text-[#ffa300] hover:underline">
            Termos de Uso
          </Link>
          {' '}e{' '}
          <Link href="/privacidade" target="_blank" rel="noopener noreferrer" className="text-[#ffa300] hover:underline">
            Política de Privacidade
          </Link>
          .
        </label>
      </div>
      {errors.acceptTerms && (
        <p className="text-red-400 text-xs -mt-2">{errors.acceptTerms.message}</p>
      )}

      <Button
        type="submit"
        disabled={isLoading}
        className="w-full"
      >
        {isLoading ? 'Criando conta...' : 'Criar conta'}
      </Button>

      {serverError && (
        <div className="p-3 bg-red-500/10 border border-red-500/50 rounded-lg text-red-400 text-sm text-center animate-in fade-in">
          {serverError}
        </div>
      )}
    </form>
  )
}

